<template>
  <div class="pre-projects-phone-list">
    <BasePhoneList
      :items="preProjects"
      :sort-open="sortOpen"
      :sort-options="sortOptions"
      :sort-key="sortKey"
      :has-more="hasMore"
      :show-end="!trialRestricted"
      :loading-more="loadingMore"
      :load-more-error="loadMoreError"
      :pending="pending"
      @update:sortOpen="$emit('update:sortOpen', $event)"
      @sort="$emit('sort', $event)"
      @load-more="$emit('load-more')"
    >
      <template #item="{ item: preProject }">
        <PreProjectListItem
          :key="preProject.id"
          :pre-project="preProject"
          :budgets="budgetsOf(preProject)"
          :actionable="!trialRestricted"
          :share-pending="sharePending(preProject.id)"
          @open="detailsId = preProject.id"
          @share="$emit('share', preProject)"
        />
      </template>
    </BasePhoneList>

    <BaseBottomSheet v-model="detailsOpen" :title="details ? details.title : ''">
      <template v-if="details">
        <div v-if="hasTags" class="pre-projects-phone-list__tags">
          <BaseTag v-if="isNew" tone="info" preset="primary">{{ $t('tags.new') }}</BaseTag>
          <BaseTag v-if="hasNumber" tone="neutral" preset="soft">#{{ details.projectNumber }}</BaseTag>
          <BaseTag v-if="details.tenantId" preset="soft" tone="info" icon="user">
            {{ $t('page.pre-projects.source.manual') }}
          </BaseTag>
        </div>

        <dl class="base-bottom-sheet__facts">
          <dt>{{ $t('page.pre-projects.data-table-titles.emitter') }}</dt>
          <dd>{{ details.displayEmitter || '—' }}</dd>

          <dt>{{ $t('page.pre-projects.form.locations-label') }}</dt>
          <dd>
            <template v-if="locations.length">
              <div v-for="(location, index) in locations" :key="index">{{ locationLabel(location) }}</div>
            </template>
            <template v-else>—</template>
          </dd>

          <dt>{{ $t('page.pre-projects.filters.budget') }}</dt>
          <dd>
            <div v-for="line in budgets" :key="line.key" class="pre-projects-phone-list__budget">
              <span class="pre-projects-phone-list__year">{{ line.when }}</span>
              <BaseTag v-if="line.period" preset="soft" tone="neutral">{{ line.period }}</BaseTag>
              <span :class="{ 'is-na': !line.hasAmount }">{{ line.amount }}</span>
              <BaseTag v-for="origin in line.origins" :key="origin" preset="soft" tone="neutral">{{ origin }}</BaseTag>
              <span v-if="line.exact" class="pre-projects-phone-list__year">{{ line.exact }}</span>
            </div>
          </dd>
        </dl>

        <ul class="base-bottom-sheet__menu">
          <li v-for="source in details.sources || []" :key="source.id">
            <button type="button" @click="act('open-source', source)">
              <BaseIcon :name="source.type === 'LINK' ? 'link' : 'file-text'" />
              <span class="pre-projects-phone-list__source">{{ source.title || source.url }}</span>
              <BaseIcon name="external-link" class="base-bottom-sheet__menu-end" />
            </button>
          </li>
          <template v-if="!trialRestricted">
            <li>
              <button type="button" :class="{ 'is-active': details.followed }" @click="act('follow')">
                <BaseIcon name="bookmark" />
                {{ details.followed ? $t('actions.unfollow') : $t('actions.follow') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                :class="{ 'is-active': details.shared }"
                :disabled="sharePending(details.id)"
                @click="act('share')"
              >
                <BaseIcon name="share-2" />
                {{ details.shared ? $t('actions.unshare') : $t('actions.share') }}
              </button>
            </li>
          </template>
          <!-- Custom (tenant-entered) pre-projects can be edited and deleted. -->
          <template v-if="details.tenantId">
            <li>
              <button type="button" @click="act('edit')">
                <BaseIcon name="edit-2" />
                {{ $t('actions.edit') }}
              </button>
            </li>
            <li>
              <button type="button" class="is-danger" @click="act('delete')">
                <BaseIcon name="trash-2" />
                {{ $t('actions.delete') }}
              </button>
            </li>
          </template>
        </ul>
      </template>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneList from '@/components/base/BasePhoneList.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import PreProjectListItem from '@/components/page-pre-projects/PreProjectListItem.vue';
import { locationsOf } from '@/util/location-util';
import { budgetLines, hasProjectNumber, isNewPreProject } from '@/util/pre-project-util';

// The sortable columns of the desktop table, as one-tap choices. The budget sorts by this
// year's cost, the first budget column.
const sortOptionsFor = (year) => [
  { key: 'newest', field: 'createdAt', order: 'desc' },
  { key: 'title-asc', field: 'title', order: 'asc' },
  { key: 'emitter-asc', field: 'emitter', order: 'asc' },
  { key: 'location-asc', field: 'city', order: 'asc' },
  { key: 'budget-desc', field: `cost:${year}`, order: 'desc', year },
];

// The phone version of the pre-projects table: compact rows, and a sheet with each one's
// details and actions (there is no pre-project page).
export default {
  name: 'PreProjectsPhoneList',
  components: {
    BaseBottomSheet,
    BaseIcon,
    BasePhoneList,
    BaseTag,
    PreProjectListItem,
  },
  props: {
    preProjects: {
      type: Array,
      required: true,
    },
    // The table's budget columns for a pre-project: `{ year, amount, period, hasAmount, exact,
    // origins, span }` each.
    budgetsOf: {
      type: Function,
      required: true,
    },
    // How the table names a place: "Name · Region".
    locationLabel: {
      type: Function,
      required: true,
    },
    sharePending: {
      type: Function,
      default: () => false,
    },
    // Trial preview: no following, sharing, sorting or further pages.
    trialRestricted: {
      type: Boolean,
      default: false,
    },
    sortOpen: {
      type: Boolean,
      default: false,
    },
    sortingField: {
      type: String,
      default: 'createdAt',
    },
    sortingOrder: {
      type: String,
      default: 'desc',
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // By id: a follow or share replaces the row's object.
      detailsId: null,
    };
  },
  computed: {
    sortOptions() {
      return sortOptionsFor(new Date().getFullYear()).map((option) => ({
        ...option,
        label: this.$t(`page.pre-projects.phone-list.sort-options.${option.key}`, { year: option.year }),
      }));
    },
    sortKey() {
      const current = this.sortOptions.find(
        (option) => option.field === this.sortingField && option.order === this.sortingOrder,
      );
      return current ? current.key : '';
    },
    details() {
      return this.detailsId ? this.preProjects.find((preProject) => preProject.id === this.detailsId) || null : null;
    },
    detailsOpen: {
      get() {
        return !!this.details;
      },
      set(open) {
        if (!open) this.detailsId = null;
      },
    },
    isNew() {
      return isNewPreProject(this.details);
    },
    hasNumber() {
      return hasProjectNumber(this.details);
    },
    hasTags() {
      return this.isNew || this.hasNumber || !!this.details.tenantId;
    },
    locations() {
      return locationsOf(this.details);
    },
    budgets() {
      return budgetLines(this.budgetsOf(this.details));
    },
  },
  methods: {
    // As in every sheet, an action closes it; its toast confirms it.
    act(action, ...args) {
      const preProject = this.details;
      this.detailsId = null;
      this.$emit(action, preProject, ...args);
    },
  },
};
</script>

<style lang="scss">
// The sheet's parts are moved out of the list, to the app's root.
.pre-projects-phone-list {
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  &__budget {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
    font-variant-numeric: tabular-nums;

    .is-na {
      color: $color-slate-500;
    }
  }

  &__year {
    font-size: 12px;
    color: $color-slate-500;
  }

  &__source {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
